import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Plus, Mail, Phone, Calendar, PawPrint } from 'lucide-react';
import { logger } from '../../utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const ClientsList = () => {
  const navigate = useNavigate();
  const password = sessionStorage.getItem('admin_password');
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!password) {
      navigate('/admin/login');
      return;
    }
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/clients`, {
        headers: {
          'Authorization': `Basic ${btoa(':' + password)}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setClients(data);
      }
    } catch (err) {
      logger.error('Failed to fetch clients:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b border-border">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-primary">Clients</h1>
              <p className="text-sm text-foreground/60">Manage client portal accounts</p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => navigate('/admin/dashboard')}
                className="px-4 py-2 text-sm border border-border rounded-lg hover:bg-gray-50"
              >
                ← Back to Dashboard
              </button>
              <button
                onClick={() => navigate('/admin/clients/new')}
                className="px-4 py-2 text-sm bg-primary text-white rounded-lg hover:bg-primary/90 flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                New Client
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-6 py-8">
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          </div>
        ) : clients.length === 0 ? (
          <div className="bg-white rounded-xl border border-border p-12 text-center">
            <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Clients Yet</h3>
            <p className="text-gray-600 mb-6">Create your first client account to get started</p>
            <button
              onClick={() => navigate('/admin/clients/new')}
              className="px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary/90"
            >
              Create First Client
            </button>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-border overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-border">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Client</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contact</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Login</th>
                  <th className="px-6 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {clients.map(client => (
                  <tr key={client.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div>
                        <p className="font-medium text-gray-900">{client.first_name} {client.last_name}</p>
                        <div className="flex items-center gap-1 text-sm text-gray-600 mt-1">
                          <Mail className="w-3 h-3" />
                          {client.email}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {client.phone && (
                        <div className="flex items-center gap-1 text-sm text-gray-600">
                          <Phone className="w-3 h-3" />
                          {client.phone}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      {client.is_active ? (
                        <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium">
                          Active
                        </span>
                      ) : (
                        <span className="px-2 py-1 bg-gray-100 text-gray-800 rounded-full text-xs font-medium">
                          Inactive
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      <div className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {new Date(client.created_at).toLocaleDateString()}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {client.last_login ? new Date(client.last_login).toLocaleDateString() : 'Never'}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-3">
                        <button
                          onClick={() => navigate(`/admin/pets?client_id=${client.id}`)}
                          data-testid={`client-pets-${client.id}`}
                          className="inline-flex items-center gap-1 text-sm text-primary hover:text-primary/80"
                          title="View pets"
                        >
                          <PawPrint className="w-4 h-4" />
                          Pets
                        </button>
                        <button
                          onClick={() => navigate(`/admin/clients/${encodeURIComponent(client.email)}/emails`)}
                          data-testid={`client-emails-${client.id}`}
                          className="inline-flex items-center gap-1 text-sm text-primary hover:text-primary/80"
                          title="View email history"
                        >
                          <Mail className="w-4 h-4" />
                          Emails
                        </button>
                        <button
                          onClick={() => navigate(`/admin/clients/${client.id}`)}
                          className="text-sm text-primary hover:text-primary/80"
                        >
                          View
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="mt-6 text-sm text-gray-600">
          Total Clients: {clients.length}
        </div>
      </div>
    </div>
  );
};
